import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import FilterPanel from "../common/FilterPanel";
import FilterChips, { countActive } from "../common/FilterChips";
import { INFLUENCER_FILTERS } from "../../config/filterConfigs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faLink,
  faCopy,
  faCheck,
  faChartLine,
  faCircleInfo,
  faLinkSlash,
  faBell,
  faCheckDouble,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

const fmt = (n) =>
  (n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const SummaryCard = ({ label, value, prefix, colorClass }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-5 py-4">
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
    <p className={`text-2xl font-bold ${colorClass || "text-gray-800"}`}>
      {prefix}{fmt(value)}
    </p>
  </div>
);

const InfluencerHub = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [influencers, setInfluencers] = useState([]);
  const [filters, setFilters] = useState({});
  const [showFilter, setShowFilter] = useState(false);
  const matchesInfluencerFilter = (inf) => {
    const q = (filters.search || "").trim().toLowerCase();
    if (q && !(`${inf.code || ""} ${inf.influencer || ""}`.toLowerCase().includes(q))) return false;
    if (filters.status === "active" && inf.active === false) return false;
    if (filters.status === "inactive" && inf.active !== false) return false;
    if (filters.status === "pending" && !(inf.pendingCommission > 0)) return false;
    return true;
  };
  const [copiedId, setCopiedId] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [scheduled, setScheduled] = useState(null); // { count, total, payouts }
  const [approvingAll, setApprovingAll] = useState(false);
  const [approvingSingleId, setApprovingSingleId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, scheduledRes] = await Promise.all([
        func.get("mst-admin/influencer/analytics"),
        func.get("mst-admin/influencer/payouts/scheduled"),
      ]);
      if (analyticsRes?.status === 200 && analyticsRes.data?.success) {
        setSummary(analyticsRes.data.summary);
        setInfluencers(analyticsRes.data.influencers || []);
      } else {
        toast.error("Failed to load influencer analytics");
      }
      if (scheduledRes?.status === 200 && scheduledRes.data?.success) {
        setScheduled({ count: scheduledRes.data.count, total: scheduledRes.data.total, payouts: scheduledRes.data.payouts || [] });
      }
    } catch {
      toast.error("Failed to load influencer analytics");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCopyLink = (inf) => {
    if (!inf.publicToken) {
      toast.warn("Generate a public link first");
      return;
    }
    const link = `${window.location.origin}/influencer/${inf.publicToken}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedId(inf._id);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleGenerateToken = async (inf) => {
    setGeneratingId(inf._id);
    try {
      const res = await func.post(`mst-admin/influencer/${inf._id}/generate-token`, {});
      if (res?.status === 200 && res.data?.success) {
        toast.success("Public link generated!");
        fetchData();
      } else {
        toast.error("Failed to generate link");
      }
    } catch {
      toast.error("Failed to generate link");
    }
    setGeneratingId(null);
  };

  const handleRevokeToken = async (inf) => {
    if (!window.confirm(`Revoke public link for ${inf.code}? The influencer will lose dashboard access immediately.`)) return;
    setRevokingId(inf._id);
    try {
      const res = await func.del(`mst-admin/influencer/${inf._id}/token`);
      if (res?.status === 200 && res.data?.success) {
        toast.success("Public link revoked");
        fetchData();
      } else {
        toast.error("Failed to revoke link");
      }
    } catch {
      toast.error("Failed to revoke link");
    }
    setRevokingId(null);
  };

  const handleApproveSingle = async (payoutId, amount, influencer) => {
    if (!window.confirm(`Approve payout of ₹${fmt(amount)} to ${influencer}? Confirm you've made the transfer.`)) return;
    setApprovingSingleId(payoutId);
    try {
      const res = await func.post(`mst-admin/influencer/payouts/${payoutId}/approve`, {});
      if (res?.status === 200 && res.data?.success) {
        toast.success(`Payout of ₹${fmt(amount)} approved`);
        fetchData();
      } else {
        toast.error(res?.data?.message || "Failed to approve payout");
      }
    } catch {
      toast.error("Failed to approve payout");
    }
    setApprovingSingleId(null);
  };

  const handleApproveAll = async () => {
    if (!window.confirm(`Approve all ${scheduled?.count} scheduled payout(s) totalling ₹${fmt(scheduled?.total)}? This confirms you have made the transfers.`)) return;
    setApprovingAll(true);
    try {
      const res = await func.post("mst-admin/influencer/payouts/approve", {});
      if (res?.status === 200 && res.data?.success) {
        toast.success(res.data.message);
        fetchData();
      } else {
        toast.error(res?.data?.message || "Failed to approve payouts");
      }
    } catch {
      toast.error("Failed to approve payouts");
    }
    setApprovingAll(false);
  };

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "-";

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <FontAwesomeIcon icon={faChartLine} className="text-white text-xl" />
          <div>
            <h1 className="text-2xl font-bold text-white">Influencer Hub</h1>
            <p className="text-white/70 text-sm">Track conversions, commissions and payouts for all influencer promo codes</p>
          </div>
        </div>

        {/* Scheduled Payouts Banner */}
        {!loading && scheduled?.count > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 mb-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faBell} className="text-amber-500 text-lg flex-shrink-0" />
                <div>
                  <p className="font-semibold text-amber-800 text-sm">
                    {scheduled.count} payout{scheduled.count !== 1 ? "s" : ""} scheduled this week — ₹{fmt(scheduled.total)} total
                  </p>
                  <p className="text-amber-600 text-xs mt-0.5">
                    Auto-generated every Saturday 8 AM. Confirm once you've made the transfers via UPI.
                  </p>
                </div>
              </div>
              <button
                onClick={handleApproveAll}
                disabled={approvingAll}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition shadow disabled:opacity-50 flex-shrink-0"
              >
                <FontAwesomeIcon icon={faCheckDouble} />
                {approvingAll ? "Processing..." : "Confirm All Paid"}
              </button>
            </div>
            {/* Per-payout rows */}
            {scheduled?.payouts?.length > 0 && (
              <div className="mt-3 divide-y divide-amber-100">
                {scheduled.payouts.map((p) => (
                  <div key={p._id} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-amber-800 font-medium">{p.influencer}</span>
                    <span className="text-amber-700">₹{fmt(p.amount)} via {p.method}</span>
                    <button
                      onClick={() => handleApproveSingle(p._id, p.amount, p.influencer)}
                      disabled={approvingSingleId === p._id}
                      className="flex items-center gap-1 px-3 py-1 text-xs bg-white border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 disabled:opacity-50"
                    >
                      <FontAwesomeIcon icon={approvingSingleId === p._id ? faSpinner : faCheck} spin={approvingSingleId === p._id} />
                      Approve
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Summary Cards */}
        {!loading && summary && (
          /* Seven cards, because "Queued" had to become visible. Pending now means
             UNCLAIMED — commission that is neither paid nor already sitting in the
             approval list. Without the Queued card beside it, a ₹0 pending next to a
             non-empty approval queue reads as "nothing is owed". */
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
            <SummaryCard label="Total Influencers" value={summary.totalInfluencers} colorClass="text-indigo-700" />
            <SummaryCard label="Total Conversions" value={summary.totalConversions} colorClass="text-blue-700" />
            <SummaryCard label="Total Revenue" value={summary.totalRevenue} prefix="₹" colorClass="text-green-700" />
            <SummaryCard label="Commission Owed" value={summary.totalCommissionOwed} prefix="₹" colorClass="text-orange-600" />
            <SummaryCard label="Commission Paid" value={summary.totalCommissionPaid} prefix="₹" colorClass="text-teal-700" />
            <SummaryCard label="Queued for Approval" value={summary.totalCommissionScheduled} prefix="₹" colorClass={summary.totalCommissionScheduled > 0 ? "text-amber-600" : "text-gray-500"} />
            <SummaryCard label="Unclaimed" value={summary.pendingCommission} prefix="₹" colorClass={summary.pendingCommission > 0 ? "text-red-600" : "text-gray-500"} />
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => setShowFilter(true)} className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition">
            Filters
            {countActive(INFLUENCER_FILTERS, filters) > 0 && (
              <span className="ml-1 inline-flex items-center justify-center text-[10px] font-bold bg-indigo-600 text-white rounded-full w-5 h-5">{countActive(INFLUENCER_FILTERS, filters)}</span>
            )}
          </button>
          <FilterChips config={INFLUENCER_FILTERS} filters={filters} onClearKeys={(keys) => { const n = { ...filters }; keys.forEach((k) => { n[k] = ""; }); setFilters(n); }} onClearAll={() => setFilters({})} />
        </div>
        <FilterPanel isOpen={showFilter} onClose={() => setShowFilter(false)} title="Filter Influencers" config={INFLUENCER_FILTERS} filters={filters} onChange={setFilters} onApply={() => setShowFilter(false)} onReset={() => setFilters({})} />

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[
                    "Code", "Handle", "Rate", "Conversions", "Revenue",
                    "Earned", "Paid", "Pending", "Last Sale", "Status", "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(4)].map((_, i) => (
                    <tr key={i} className="border-b border-gray-100 animate-pulse">
                      {[...Array(11)].map((_, j) => (
                        <td key={j} className="px-4 py-3">
                          <div className="h-4 bg-gray-200 rounded" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : influencers.filter(matchesInfluencerFilter).length === 0 ? (
                  <tr>
                    <td colSpan={11} className="py-16 text-center text-gray-400">
                      <FontAwesomeIcon icon={faUsers} className="text-3xl mb-3 block mx-auto opacity-30" />
                      No influencer promo codes yet. Create a promo code with an influencer handle to get started.
                    </td>
                  </tr>
                ) : (
                  influencers.filter(matchesInfluencerFilter).map((inf) => (
                    <tr key={inf._id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                      <td className="px-4 py-3 font-mono font-semibold text-gray-800">{inf.code}</td>
                      <td className="px-4 py-3 text-gray-700">{inf.influencer || "-"}</td>
                      <td className="px-4 py-3 text-gray-600">{inf.commissionRate}%</td>
                      <td className="px-4 py-3 font-semibold text-indigo-700">{inf.conversions}</td>
                      <td className="px-4 py-3 font-semibold text-gray-800">₹{fmt(inf.revenue)}</td>
                      <td className="px-4 py-3 text-orange-600 font-semibold">₹{fmt(inf.commissionOwed)}</td>
                      <td className="px-4 py-3 text-teal-700 font-semibold">₹{fmt(inf.commissionPaid)}</td>
                      <td className="px-4 py-3">
                        <span className={`font-semibold ${inf.pendingCommission > 0 ? "text-red-600" : "text-gray-400"}`}>
                          ₹{fmt(inf.pendingCommission)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {fmtDate(inf.lastConversionAt)}
                      </td>
                      <td className="px-4 py-3">
                        {inf.active !== false ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Active</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-600">Inactive</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <button
                            onClick={() => navigate(`/influencer-hub/${inf._id}`)}
                            className="px-2.5 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-medium transition flex items-center gap-1"
                            title="View Details"
                          >
                            <FontAwesomeIcon icon={faCircleInfo} />
                            Details
                          </button>
                          {inf.publicToken ? (
                            <>
                              <button
                                onClick={() => handleCopyLink(inf)}
                                className="px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition flex items-center gap-1"
                                title="Copy Public Link"
                              >
                                <FontAwesomeIcon icon={copiedId === inf._id ? faCheck : faCopy} />
                                {copiedId === inf._id ? "Copied!" : "Copy Link"}
                              </button>
                              <button
                                onClick={() => handleRevokeToken(inf)}
                                disabled={revokingId === inf._id}
                                className="px-2.5 py-1 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded-md font-medium transition flex items-center gap-1 disabled:opacity-50"
                                title="Revoke Public Link"
                              >
                                <FontAwesomeIcon icon={faLinkSlash} />
                                {revokingId === inf._id ? "Revoking..." : "Revoke"}
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleGenerateToken(inf)}
                              disabled={generatingId === inf._id}
                              className="px-2.5 py-1 text-xs bg-amber-500 hover:bg-amber-600 text-white rounded-md font-medium transition flex items-center gap-1 disabled:opacity-50"
                              title="Generate Public Link"
                            >
                              <FontAwesomeIcon icon={faLink} />
                              {generatingId === inf._id ? "Generating..." : "Gen Link"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfluencerHub;
